// Oklusi mover oleh bangunan: lapisan scratch + mask siluet bangunan di depannya.
import { TILE_W, TILE_H } from "./iso";
import { refHeight } from "./paint";
import { paintBuildingTo } from "./buildingPaint";

export function createOcclusion({ canvas, cam, gridToScreen, getDpr, getCtx, getBldTileMap }) {
  // ---- Oklusi mover oleh bangunan: MASK SILUET ----
  // Mover (mobil/warga) digambar ke kanvas scratch, lalu bagian yang tertutup bangunan
  // DI DEPANNYA dihapus (destination-out) memakai sprite siluet bangunan itu sendiri —
  // bentuknya persis (bukan kotak), tanpa melukis ulang bangunan (tidak merusak urutan
  // oklusi antar-bangunan). Sprite siluet di-cache per bid/varian/rot pada zoom terbatas
  // (MASK_ZOOM) dan diskalakan saat dipakai — hemat memori, tepi sedikit lembut.
  const MASK_ZOOM = 0.8;
  const maskSprites = new Map();
  function maskSprite(b, def) {
    const rot = b.rot || 0;
    const key = `${def?.bid}|${b.vid || ""}|${rot}`;
    let sp = maskSprites.get(key);
    if (sp) return sp;
    let w = def?.w || 1, h = def?.h || 1;
    if (rot % 2 === 1) [w, h] = [h, w];
    const z = MASK_ZOOM, hw = (TILE_W / 2) * z, hh = (TILE_H / 2) * z;
    const height = refHeight(def?.floors ?? 1, w, h, z);
    // bbox tapak+tinggi (+margin untuk detail di luar prisma) relatif anchor (b.x,b.y)
    const mx = TILE_W * z * 0.5, my = TILE_H * z * 1.5 + height * 0.6;
    const x0 = Math.floor(-h * hw - mx), y0 = Math.floor(-height - my);
    const sw = Math.ceil(w * hw + mx) - x0, sh = Math.ceil((w + h) * hh + TILE_H * z) - y0;
    const cv = document.createElement("canvas");
    cv.width = sw; cv.height = sh;
    const g = cv.getContext("2d");
    g.translate(-x0, -y0);
    const proj = (gx, gy) => [((gx - b.x) - (gy - b.y)) * hw, ((gx - b.x) + (gy - b.y)) * hh];
    paintBuildingTo(g, b, def, proj, z, 0);
    sp = { cv, dx: x0 / z, dy: y0 / z, sw: sw / z, sh: sh / z }; // satuan "per zoom"
    if (maskSprites.size > 200) maskSprites.clear();
    maskSprites.set(key, sp);
    return sp;
  }
  // Bangunan di depan titik (gx,gy) yang kotak layarnya menyentuh sprite mover radius r.
  // "Di depan" (iso): tapak dimulai di +x atau +y setelah tile mover. Dicari lewat
  // bldTileMap pada jendela diagonal 7 tile (gedung tinggi jauh di depan ikut terdeteksi).
  function occludersFor(gx, gy, sx, sy, r) {
    const tx = Math.floor(gx), ty = Math.floor(gy);
    const out = [], seen = new Set();
    for (let dx = 0; dx <= 6; dx++) for (let dy = 0; dy <= 6; dy++) {
      if ((dx === 0 && dy === 0) || Math.abs(dx - dy) > 2) continue;
      const hit = getBldTileMap()[(tx + dx) + "," + (ty + dy)];
      if (!hit || seen.has(hit.b)) continue;
      seen.add(hit.b);
      const b = hit.b, def = hit.def;
      let w = def?.w || 1, h = def?.h || 1;
      if ((b.rot || 0) % 2 === 1) [w, h] = [h, w];
      if (!(b.x >= tx + 1 || b.y >= ty + 1)) continue; // di belakang/sejajar: tidak menutupi
      const [ax, ay] = gridToScreen(b.x, b.y);
      const z = cam.zoom, hw = (TILE_W / 2) * z, hh = (TILE_H / 2) * z;
      const H = refHeight(def?.floors ?? 1, w, h, z);
      const x0 = ax - h * hw - hw, x1 = ax + w * hw + hw, y0 = ay - H * 1.6 - TILE_H * z, y1 = ay + (w + h) * hh + TILE_H * z;
      if (x1 < sx - r || x0 > sx + r || y1 < sy - r || y0 > sy + r) continue;
      out.push({ b, def, ax, ay });
    }
    return out;
  }
  // Lapisan mover: SATU kanvas scratch seukuran layar per frame. Mover digambar urut
  // kedalaman; tepat setelah tiap mover, siluet bangunan di depannya dihapus (destination-out,
  // dibatasi kotak mover itu). Mover yang lebih depan digambar sesudahnya sehingga tidak ikut
  // terhapus. Satu blit ke kanvas utama di akhir → murah (tanpa flush per mover).
  let scratch = null, scratchCtx = null, layerActive = false;
  const stats = { begin: 0, draw: 0, find: 0, mask: 0, end: 0, nOcc: 0, nMover: 0 };
  function beginMoverLayer() {
    const t0 = performance.now();
    if (!scratch) { scratch = document.createElement("canvas"); scratchCtx = scratch.getContext("2d"); }
    // Resolusi scratch: pada zoom kecil (mover ≤ ~19 px) cukup dpr 1 — clear + blit layar penuh 4× lebih murah di HP retina
    const sdpr = cam.zoom < 0.6 ? 1 : getDpr();
    const sw = Math.round(canvas.clientWidth * sdpr), sh = Math.round(canvas.clientHeight * sdpr);
    if (scratch.width !== sw || scratch.height !== sh) { scratch.width = sw; scratch.height = sh; }
    const g = scratchCtx;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, scratch.width, scratch.height);
    g.setTransform(sdpr, 0, 0, sdpr, 0, 0);
    layerActive = true;
    stats.begin += performance.now() - t0;
  }
  function endMoverLayer(W, H) {
    if (!layerActive) return;
    layerActive = false;
    const t0 = performance.now();
    getCtx().drawImage(scratch, 0, 0, scratch.width, scratch.height, 0, 0, W, H);
    stats.end += performance.now() - t0;
  }
  function drawOccluded(gx, gy, r, drawFn) {
    const [sx, sy] = gridToScreen(gx, gy);
    const g = layerActive ? scratchCtx : getCtx();
    let t0 = performance.now();
    drawFn(g, sx, sy);
    stats.draw += performance.now() - t0; stats.nMover++;
    if (!layerActive || !getBldTileMap()) return;
    t0 = performance.now();
    const occ = occludersFor(gx, gy, sx, sy, r);
    stats.find += performance.now() - t0;
    if (!occ.length) return;
    t0 = performance.now(); stats.nOcc += occ.length;
    g.save();
    g.beginPath(); g.rect(sx - r, sy - r, r * 2, r * 2); g.clip();
    g.globalCompositeOperation = "destination-out";
    g.imageSmoothingEnabled = false; // mask: sampling nearest (murah saat diskalakan)
    const f = cam.zoom; // sprite mask disimpan per-zoom → skala ke zoom saat ini
    for (const o of occ) {
      const sp = maskSprite(o.b, o.def);
      g.drawImage(sp.cv, o.ax + sp.dx * f, o.ay + sp.dy * f, sp.sw * f, sp.sh * f);
    }
    g.restore();
    stats.mask += performance.now() - t0;
  }
  return { beginMoverLayer, endMoverLayer, drawOccluded, stats };
}
